import { useEffect, useRef, useState } from 'react'
import {
  Folder,
  FileText,
  Image as ImageIcon,
  Upload,
  Trash2,
  Download,
} from 'lucide-react'
import { redirectToLogin } from '../lib/api.js'
import { uploadFile } from '../lib/uploadDirect.js'
import { generateThumbnail } from '../lib/thumbnail.js'
import { isGeneratedDoc } from './DocumentGenerator.jsx'
import {
  Badge, Button, ConfirmDialog, EmptyState, ErrorState, IconButton, Loading, Select,
  fmtBytes, fmtDate, useToast,
} from '../kit'

export const DOC_TYPES = [
  { value: '',                  label: '— ประเภท —' },
  // Manually-tagged categories
  { value: 'xray',              label: 'X-ray' },
  { value: 'photo',             label: 'รูปถ่าย' },
  { value: 'treatment_plan',    label: 'แผนการรักษา' },
  { value: 'lab',               label: 'ผลตรวจ Lab' },
  // Generated-document types (kept in sync with DocumentGenerator's DOC_DEFS) —
  // so a PDF created in the Documents section shows its type here automatically.
  { value: 'consent',           label: 'ใบยินยอม' },
  { value: 'receipt',           label: 'ใบเสร็จ' },
  { value: 'invoice',           label: 'ใบแจ้งหนี้' },
  { value: 'fee_detail',        label: 'รายละเอียดค่ารักษา' },
  { value: 'medical_cert',      label: 'ใบรับรองแพทย์' },
  { value: 'referral',          label: 'ใบส่งตัว' },
  { value: 'xray_referral',     label: 'ใบส่งเอ็กซเรย์' },
  { value: 'toothtaxi_checkup', label: 'ตรวจสุขภาพ Tooth taxi' },
  { value: 'other',             label: 'อื่นๆ' },
]
export const DOC_TYPE_LABEL = Object.fromEntries(DOC_TYPES.map((t) => [t.value, t.label]))

// Some generated docs were stamped with a non-canonical doc_type (e.g. the
// signed-consent flow writes the Thai label 'ใบยินยอม'). Map those aliases onto
// the canonical value so the type chip/dropdown resolves correctly.
const DOC_TYPE_ALIASES = {
  'ใบยินยอม':        'consent',
  'ใบเสร็จ':          'receipt',
  'ใบรับรองแพทย์':     'medical_cert',
  'ใบส่งตัว':          'referral',
  'ใบส่งเอ็กซเรย์':     'xray_referral',
  'ใบแจ้งหนี้':        'invoice',
}
export function normalizeDocType(value) {
  if (!value) return ''
  if (DOC_TYPE_LABEL[value] != null) return value          // already canonical
  return DOC_TYPE_ALIASES[value] || value                  // alias → canonical, else as-is
}

/**
 * Document attachments on a chart. Stored on `chart.document[]` (singular field,
 * plural data — Xano convention) as objects:
 *   { kind, name, key, mime, size, user_id, employee_id, code, timestamp }
 * where `key` is the Wasabi object key. Display URL is `/api/files/{key}` —
 * a Pages Function that streams the file with session auth.
 *
 * v0 supports: upload arbitrary files + delete.
 * Future kinds: 'receipt' | 'consent' | 'plan' (generated PDFs).
 */
export default function DocumentEditor({ chart, hn, canEdit, onSaved }) {
  const docs = Array.isArray(chart?.document) ? chart.document : []
  // Generated PDFs (ใบเสร็จ / ใบรับรองแพทย์ / …) already live in the Documents
  // section, so don't repeat them here. Keep original indices so edit/delete
  // still target the right entry in chart.document[].
  const visibleDocs = docs.map((d, i) => ({ d, i })).filter(({ d }) => !isGeneratedDoc(d))
  const chartId = chart?.id
  const fileInputRef = useRef(null)

  const [uploading, setUploading] = useState(false)
  const [err, setErr] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null) // doc index
  // While the delete runs the dialog stays open (busy) with the name it was confirmed
  // for — `docs` may refresh underneath it.
  const [deletingName, setDeletingName] = useState(null)
  const [dragOver, setDragOver] = useState(false)
  const dragCounter = useRef(0)
  const toast = useToast()

  function onSectionDragEnter(e) {
    if (!canEdit || !chartId) return
    if (!e.dataTransfer?.types?.includes('Files')) return
    dragCounter.current += 1
    setDragOver(true)
  }
  function onSectionDragLeave() {
    dragCounter.current -= 1
    if (dragCounter.current <= 0) {
      dragCounter.current = 0
      setDragOver(false)
    }
  }
  function onSectionDragOver(e) {
    if (canEdit && chartId && e.dataTransfer?.types?.includes('Files')) e.preventDefault()
  }
  function onSectionDrop(e) {
    e.preventDefault()
    dragCounter.current = 0
    setDragOver(false)
    if (canEdit && chartId && e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files)
  }

  async function patchDocuments(nextDocs) {
    const r = await fetch(`/api/charts/${chartId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ document: nextDocs }),
    })
    if (r.status === 401) { redirectToLogin(); return false }
    if (!r.ok) {
      const t = await r.text().catch(() => '')
      throw new Error(`HTTP ${r.status}: ${t || r.statusText}`)
    }
    return true
  }

  // Sends the bytes straight to Wasabi when the bucket allows it, and falls back
  // to the old multipart proxy when it does not — see src/lib/uploadDirect.js.
  // An X-ray is the biggest file this clinic produces and this is the route it
  // arrives on, so this is the call site that most needed to stop routing whole
  // files through a Worker isolate shared with every other /api request.
  async function uploadOne(blob, fileName, kind) {
    return uploadFile('/api/upload', { hn: String(hn), kind, name: fileName }, blob)
  }

  async function handleFiles(fileList) {
    if (!chartId) { setErr('ไม่พบ chart_id'); return }
    if (!hn) { setErr('ไม่พบ HN ของผู้ป่วย'); return }
    if (!fileList || fileList.length === 0) return
    setUploading(true); setErr(null)
    try {
      const newRecords = []
      for (const file of Array.from(fileList)) {
        // Generate a thumbnail in parallel with the original upload (images only).
        const thumbPromise = generateThumbnail(file)
        const originalPromise = uploadOne(file, file.name, 'original')

        const [original, thumbBlob] = await Promise.all([originalPromise, thumbPromise])

        let thumb = null
        if (thumbBlob) {
          try {
            thumb = await uploadOne(thumbBlob, `thumb-${file.name}.webp`, 'thumb')
          } catch { /* non-fatal: original is still usable */ }
        }

        newRecords.push({
          // Match the Xano oral_exam-style record shape requested:
          url:        original.url,
          location:   original.location,
          name:       original.name,
          note:       '',
          size:       original.size,
          thumbnail:  thumb?.location || '',
          doc_type:   '',
          type:       original.type,
          // created_at / modified_at / user_id stamped by the chart PATCH proxy.
        })
      }
      const nextDocs = [...docs, ...newRecords]
      const ok = await patchDocuments(nextDocs)
      if (ok) { toast.success('อัปโหลดแล้ว'); onSaved?.() }
    } catch (e) {
      if (e?.message !== 'unauthorized') {
        const msg = e?.message || 'อัปโหลดไม่สำเร็จ'
        setErr(msg)
        toast.error('อัปโหลดไม่สำเร็จ', { detail: msg })
      }
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function updateDoc(idx, patch) {
    setErr(null)
    try {
      const nextDocs = docs.map((d, i) => i === idx ? { ...d, ...patch } : d)
      const ok = await patchDocuments(nextDocs)
      if (ok) { toast.success('บันทึกแล้ว'); onSaved?.() }
    } catch (e) {
      const msg = e?.message || 'อัปเดตไม่สำเร็จ'
      setErr(msg)
      toast.error('บันทึกไม่สำเร็จ', { detail: msg })
    }
  }

  async function handleDelete(idx) {
    setErr(null)
    const target = docs[idx]
    if (!target) return
    try {
      // 1) Patch chart first — even if the Wasabi delete fails, the user can't
      //    re-trigger via the UI because the row is gone (orphan cleanup can run later).
      const nextDocs = docs.filter((_, i) => i !== idx)
      const ok = await patchDocuments(nextDocs)
      if (!ok) return
      // 2) Best-effort delete of the underlying Wasabi objects (original + thumb).
      const toRemove = [target.location || target.key, target.thumbnail].filter(Boolean)
      for (const k of toRemove) {
        try {
          await fetch(`/api/files/${k.split('/').map(encodeURIComponent).join('/')}`, {
            method: 'DELETE',
            credentials: 'include',
          })
        } catch { /* non-fatal */ }
      }
      toast.success('ลบไฟล์แล้ว')
      onSaved?.()
    } catch (e) {
      const msg = e?.message || 'ลบไม่สำเร็จ'
      setErr(msg)
      toast.error('ลบไม่สำเร็จ', { detail: msg })
    }
  }

  return (
    <section
      onDragEnter={onSectionDragEnter}
      onDragLeave={onSectionDragLeave}
      onDragOver={onSectionDragOver}
      onDrop={onSectionDrop}
      className={`relative bg-white rounded-2xl transition-colors ${
        dragOver ? 'border-2 border-dashed border-brand-400 ring-4 ring-brand-500/15' : 'border border-slate-200'
      }`}
    >
      {dragOver && (
        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-2xl bg-brand-50/80 pointer-events-none">
          <div className="flex items-center gap-2 text-brand-700 font-semibold text-[16px]">
            <Upload size={18} strokeWidth={2.25} /> วางไฟล์เพื่ออัปโหลด
          </div>
        </div>
      )}
      <header className="px-5 pt-4 pb-3 flex items-center gap-2.5 border-b border-slate-100">
        <span className="w-7 h-7 rounded-lg bg-slate-50 text-slate-500 flex items-center justify-center ring-1 ring-slate-100">
          <Folder size={14} strokeWidth={2} />
        </span>
        <div className="flex-1">
          <div className="text-[12.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">File</div>
          <div className="text-[13.5px] text-slate-400 -mt-0.5">ไฟล์แนบของเคส</div>
        </div>
        {canEdit && chartId && (
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
            <Button variant="soft" size="sm" icon={Upload} loading={uploading} onClick={() => fileInputRef.current?.click()}>
              {uploading ? 'กำลังอัปโหลด…' : 'อัปโหลดไฟล์'}
            </Button>
          </div>
        )}
      </header>

      <div className="p-4 sm:p-5 space-y-3">
        {err && <ErrorState title={err} />}

        {uploading && (
          <div className="px-3 py-2 rounded-lg bg-slate-50 ring-1 ring-slate-200">
            <Loading variant="inline" label="กำลังอัปโหลด…" />
          </div>
        )}

        {visibleDocs.length === 0 && !uploading && (
          <EmptyState
            icon={Upload}
            title={!canEdit || !chartId ? 'ไม่มี chart ในเคสนี้' : 'ยังไม่มีไฟล์ในเคสนี้'}
            hint={!canEdit || !chartId ? undefined : 'ลากไฟล์มาวางที่การ์ดนี้ หรือกดปุ่ม "อัปโหลดไฟล์"'}
          />
        )}

        {visibleDocs.length > 0 && (
          <div className="rounded-xl bg-slate-50/50 divide-y divide-slate-100 [&>*:first-child]:rounded-t-xl [&>*:last-child]:rounded-b-xl">
            {visibleDocs.map(({ d, i }) => (
              <DocumentRow
                key={`${d.location || d.url || i}-${i}`}
                doc={d}
                canEdit={canEdit}
                onUpdate={(patch) => updateDoc(i, patch)}
                onDelete={() => setConfirmDelete(i)}
              />
            ))}
          </div>
        )}
      </div>

      {(confirmDelete != null || deletingName != null) && (
        <ConfirmDialog
          title={`ลบไฟล์ "${deletingName != null ? deletingName : docs[confirmDelete]?.name}"?`}
          message="ไฟล์จะถูกลบจาก chart และจาก storage ทันที (ไม่สามารถยกเลิกได้)"
          confirmLabel="ลบไฟล์"
          tone="danger"
          busy={deletingName != null}
          onConfirm={async () => {
            if (deletingName != null) return
            const i = confirmDelete
            setDeletingName(String(docs[i]?.name ?? ''))
            try { await handleDelete(i) } finally { setDeletingName(null); setConfirmDelete(null) }
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Atoms                                                                       */
/* -------------------------------------------------------------------------- */

function DocumentRow({ doc, canEdit, onUpdate, onDelete }) {
  const name      = doc.name || 'untitled'
  const mime      = doc.type || doc.mime || ''
  const location  = doc.location || doc.key || ''
  const thumbLoc  = doc.thumbnail || ''
  const fullUrl   = location ? `/api/files/${encodeKey(location)}` : (doc.url || '')
  const thumbUrl  = thumbLoc ? `/api/files/${encodeKey(thumbLoc)}` : ''
  const isImage   = /^image\//.test(mime) || /\.(png|jpe?g|webp|gif|heic)$/i.test(name)
  const previewUrl = thumbUrl || (isImage ? fullUrl : '')

  // Local input state so typing doesn't trigger a server PATCH on every keystroke.
  const [noteLocal, setNoteLocal] = useState(doc.note || '')
  useEffect(() => { setNoteLocal(doc.note || '') }, [doc.note])
  function commitNote() {
    const v = noteLocal.trim()
    if (v !== (doc.note || '')) onUpdate?.({ note: v })
  }

  return (
    <div className="flex items-start gap-3 px-3 py-2.5 hover:bg-white transition-colors">
      <a
        href={fullUrl || undefined}
        target={fullUrl ? '_blank' : undefined}
        rel="noopener noreferrer"
        className="w-11 h-11 rounded-lg overflow-hidden flex-none flex items-center justify-center ring-1 ring-slate-100 bg-slate-50 mt-0.5"
      >
        {previewUrl
          ? <img src={previewUrl} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" loading="lazy" />
          : isImage
            ? <ImageIcon size={16} className="text-slate-500" strokeWidth={1.75} />
            : <FileText size={16} className="text-slate-500" strokeWidth={1.75} />}
      </a>

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-[14px] font-medium text-slate-800 truncate flex-1 min-w-0">{name}</div>
          {canEdit
            ? (
              <div className="w-44 flex-none">
                <Select
                  value={normalizeDocType(doc.doc_type)}
                  options={DOC_TYPES}
                  placeholder="— ประเภท —"
                  onChange={(v) => onUpdate?.({ doc_type: v })}
                />
              </div>
            )
            : doc.doc_type
              ? <Badge>{DOC_TYPE_LABEL[normalizeDocType(doc.doc_type)] || doc.doc_type}</Badge>
              : null}
        </div>

        <div className="text-[12.5px] text-slate-400 flex items-center gap-1.5">
          {doc.size ? <span>{fmtBytes(doc.size)}</span> : null}
          {doc.created_at && <span>· {fmtDate(doc.created_at)}</span>}
        </div>

        {canEdit ? (
          <input
            type="text"
            value={noteLocal}
            onChange={(e) => setNoteLocal(e.target.value)}
            onBlur={commitNote}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur() }}
            placeholder="หมายเหตุ (เลือกใส่)"
            className="w-full px-2 py-1 text-[14px] border border-transparent rounded-md hover:border-slate-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/15 outline-none bg-transparent focus:bg-white placeholder:text-slate-300 placeholder:italic"
          />
        ) : (
          doc.note && <div className="text-[14px] text-slate-600 px-2 py-1">{doc.note}</div>
        )}
      </div>

      <div className="flex items-center gap-1 flex-none mt-0.5">
        {fullUrl && (
          <a href={fullUrl} target="_blank" rel="noopener noreferrer"
            className="text-slate-400 hover:text-brand-600 transition-colors p-1.5 rounded-md hover:bg-brand-50"
            title="เปิด / ดาวน์โหลด">
            <Download size={14} strokeWidth={2} />
          </a>
        )}
        {canEdit && (
          <IconButton icon={Trash2} size="sm" variant="danger-soft" onClick={onDelete} label="ลบไฟล์" />
        )}
      </div>
    </div>
  )
}

function encodeKey(k) {
  return String(k).split('/').map(encodeURIComponent).join('/')
}
